import React, { useRef, useState } from "react";
import Link from "next/link";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";

import departments from "@/data/careers/departments.json";
import locations from "@/data/careers/locations.json";
import jobs from "@/data/careers/jobs.json";
import { routes } from "@/utils/routes";
import classes from "@/styles/careers/careers-main.module.css";
import Image from "next/image";
import { carImg1, carImg2 } from "@/utils/images";
import { useIsMobile } from "@/hooks/useIsMobile";

const CareersMain = () => {
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const jobsRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  const handleDepartmentChange = (newDep: string) => () => {
    setDepartmentFilter(newDep);
  };

  const handleLocationChange = (newLoc: string) => () => {
    setLocationFilter(newLoc);
  };

  const handleCTAClick = () => {
    if (jobsRef.current) {
      window.scrollTo({ top: jobsRef.current.offsetTop, behavior: "smooth" });
    }
  };

  return (
    <main className={classes.classes}>
      <section className={classes.hero}>
        <Container className="py-5">
          <Row>
            <Col className={classes.blueCol} xs={12} lg={6}>
              <div>
                <h2>Are you a Passionate Individual?</h2>
                <p>
                  The next step in your career could be working here, with other
                  bright people at ShipVista.
                </p>
                <Button onClick={handleCTAClick}>See our open positions</Button>
              </div>
            </Col>
            <Col className={classes.imgCol} xs={12} lg={6}>
              <Image
                src={carImg1}
                alt={carImg1}
                className="img-fluid"
                height={250}
                width={400}
              />
            </Col>
          </Row>
        </Container>
      </section>
      <section className={`py-5 ${isMobile ? "" : "my-5 "}`}>
        <Container>
          <Row>
            <Col className={classes.blueCol} xs={12} lg={6}>
              <div>
                <h3>Who are the people of ShipVista?</h3>
                <p>
                  We are a small but dedicated group of developers, designers,
                  analyst, and technologist who have come together to build a
                  product to help merchants and customers in eCommerce have a
                  better shipping experience.
                </p>
              </div>
            </Col>
            <Col className={classes.imgCol} xs={12} lg={6}>
              <Image
                src={carImg2}
                alt="About"
                className="img-fluid"
                height={250}
                width={400}
              />
            </Col>
          </Row>
        </Container>
      </section>
      <section className="container">
        <h3 className="text-center text-secondary">
          What We Value In Our Team
        </h3>
        <div className="my-5">
          <div className={classes.tableGrid}>
            <div
              className={`${isMobile ? "border" : "border-end border-bottom"}`}
            >
              <h5 className="text-secondary">Balance</h5>
              <p></p>
            </div>
            <div
              className={`${isMobile ? "border" : "border-end border-bottom"}`}
            >
              <h5 className="text-secondary">Innovation</h5>
              <p></p>
            </div>
            <div className={`${isMobile ? "border" : "border-bottom"}`}>
              <h5 className="text-secondary">Teamwork</h5>
              <p></p>
            </div>
          </div>
          <div className={classes.tableGrid}>
            <div className={`${isMobile ? "border" : "border-end"}`}>
              <h5 className="text-secondary">Accountability</h5>
              <p></p>
            </div>
            <div className={`${isMobile ? "border" : "border-end"}`}>
              <h5 className="text-secondary">Growth</h5>
              <p></p>
            </div>
            <div className={` ${isMobile ? "border" : ""}`}>
              <h5 className="text-secondary">Wellbeing</h5>
              <p></p>
            </div>
          </div>
        </div>
      </section>
      <section className="my-5 container" ref={jobsRef}>
        <h3 className="text-center text-secondary">Our Current Open Roles</h3>
        <Row>
          <Col xs={12} lg={6}>
            <h4
              className={`text-center text-secondary ${
                isMobile ? "my-3" : "my-5"
              }`}
            >
              Department
            </h4>
            <div className="d-flex justify-content-center align-items-center my-3 flex-wrap">
              <button
                className={`${
                  departmentFilter === "all" ? "btn text-green" : "btn"
                }`}
                onClick={handleDepartmentChange("all")}
              >
                All
              </button>
              {Object.entries(departments).map((dep) => {
                const [key, { label }] = dep;

                return (
                  <button
                    key={key}
                    className={`${
                      departmentFilter === key ? "btn text-green" : "btn"
                    }`}
                    onClick={handleDepartmentChange(key)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <h4
              className={`text-center text-secondary ${
                isMobile ? "my-3" : "my-5"
              }`}
            >
              Location
            </h4>
            <div className="d-flex justify-content-center align-items-center flex-wrap">
              <button
                className={`${
                  locationFilter === "all" ? "btn text-green" : "btn"
                }`}
                onClick={handleLocationChange("all")}
              >
                All
              </button>
              {Object.entries(locations).map((loc) => {
                const [key, { label }] = loc;
                return (
                  <button
                    key={key}
                    className={`${
                      locationFilter === key ? "btn text-green" : "btn"
                    }`}
                    onClick={handleLocationChange(key)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </Col>
        </Row>
        <Row>
          {jobs
            .filter(
              ({ department, location }) =>
                (departmentFilter !== "all"
                  ? department === departmentFilter
                  : true) &&
                (locationFilter !== "all" ? location === locationFilter : true)
            )
            .map(({ title, id, location, department }) => {
              const locationLabel =
                (locations as any)[location]?.fullLocation || "Error";
              const departmentLabel =
                (departments as any)[department]?.label || "Error";
              return (
                <Col className="mb-3" xs={12} md={4} key={title + "-" + id}>
                  <Link href={routes.careers + "/" + id}>
                    <Card className={classes.card}>
                      <CardBody>
                        <div className="d-flex">
                          <h6 className="text-secondary">{title}</h6>
                          <p className="text-green text-end">{locationLabel}</p>
                        </div>
                        <div className="btn btn-green float-end btn-sm">
                          {departmentLabel}
                        </div>
                      </CardBody>
                    </Card>
                  </Link>
                </Col>
              );
            })}
        </Row>
      </section>
    </main>
  );
};

export default CareersMain;
